import { type Dispatch, type SetStateAction, useState } from 'react'

import type { NDParamsType } from 'commons'
import { downloadFile, FileType } from 'commons/utils/file'
import { Alert, DatePicker, Icon, Radio, SlimButton } from 'components'
import { formattingNDPayload } from 'container/NDPage/utils'
import dayjs from 'dayjs'
import { ModalContentLayout } from 'layouts/ModalContentLayout'
import { Controller, useForm, useFormContext } from 'react-hook-form'
import { toast } from 'react-toastify'
import { downloadCurrentNDReport, requestNDHistory } from 'services/nd'
import { setLoadingReport } from 'store/NegotiationsContext/negotiationsSlice'
import { useDispatch, useSelector } from 'store/store'

import * as S from './styles'

type ReportModalProps = {
  open: boolean
  setOpen: Dispatch<SetStateAction<boolean>>
  setHistoryRequestModal: Dispatch<SetStateAction<boolean>>
}

type ReportKind = 'pending' | 'history'

// TODO: replace direct dayjs() usage with utils

const ModalReport = ({ open, setOpen, setHistoryRequestModal }: ReportModalProps) => {
  const dispatch = useDispatch()
  const [selectedReport, setSelectedReport] = useState<ReportKind>('pending')
  const { loadingReport } = useSelector((state) => state.negotiations)
  const defaultValues = {
    reportDate: dayjs().format('YYYY-MM'),
  }

  const { getValues: getFilterValues } = useFormContext()

  const { control, trigger, handleSubmit, reset, formState } = useForm({
    defaultValues,
    mode: 'onChange',
  })

  const handleClose = () => {
    setOpen(false)
    setSelectedReport('pending')
    reset(defaultValues)
  }

  const getCurrentNDReport = async () => {
    try {
      handleClose()
      dispatch(setLoadingReport(true))
      toast.info('O download começará em instantes!')

      const payload = formattingNDPayload(getFilterValues() as NDParamsType)
      const file = await downloadCurrentNDReport(payload)
      downloadFile({
        file,
        fileName: 'negociacoes_diferenciadas.xlsx',
        fileType: FileType.Excel,
      })
      toast.success('Relatório baixado com sucesso!')
    } catch (error) {
      toast.error('Não foi possível baixar o relatório. Tente novamente mais tarde.')
    } finally {
      dispatch(setLoadingReport(false))
    }
  }

  const submitForm = handleSubmit(async (data) => {
    try {
      dispatch(setLoadingReport(true))
      const response = await requestNDHistory(data.reportDate)
      if (response.status === 202) {
        setHistoryRequestModal(true)
      }
    } catch (error) {
      toast.error('Não foi possível solicitar o relatório. Tente novamente mais tarde.')
    } finally {
      dispatch(setLoadingReport(false))
      handleClose()
    }
  })

  const validateMinDate = (date: string) => {
    // NOTE: valid dates start from January two years prior
    return (
      dayjs(date)
        .endOf('month')
        .isAfter(dayjs().startOf('year').subtract(2, 'year').startOf('month')) || 'Data inválida.'
    )
  }

  const validateMaxDate = (date: string) => {
    return (
      dayjs(date).startOf('month').isBefore(dayjs().add(1, 'year').endOf('month')) ||
      'Data inválida.'
    )
  }

  return (
    <>
      {open && (
        <ModalContentLayout closeAction={handleClose} title="Baixar relatório" titleSize={24}>
          <S.Content>
            <S.RadioGroup>
              <Radio
                checked={selectedReport === 'pending'}
                label="Pendentes"
                name="report_type"
                onChange={() => {
                  setSelectedReport('pending')
                  reset(defaultValues)
                }}
                value="pending"
              />
              <Radio
                checked={selectedReport === 'history'}
                label="Histórico"
                name="report_type"
                onChange={() => setSelectedReport('history')}
                value="history"
              />
            </S.RadioGroup>

            {selectedReport === 'history' && (
              <>
                <S.Divider />
                <span>Selecione 1 mês cota:</span>
                <S.FieldsWrapper>
                  <Controller
                    control={control}
                    name="reportDate"
                    render={({ field, fieldState }) => (
                      <DatePicker
                        {...field}
                        disableMaskedInput={false}
                        error={fieldState.error?.message}
                        height={30}
                        inputFormat="MM/YYYY"
                        label="Mês cota"
                        maxDate={dayjs().add(1, 'year') as unknown as string}
                        minDate={dayjs().startOf('year').subtract(2, 'year') as unknown as string}
                        onChange={(date) => {
                          field.onChange(dayjs(date).format('YYYY-MM'))
                          trigger()
                        }}
                        views={['year', 'month']}
                      />
                    )}
                    rules={{
                      validate: {
                        max: validateMaxDate,
                        min: validateMinDate,
                      },
                    }}
                  />
                </S.FieldsWrapper>

                {!formState.isValid && (
                  <Alert fullWidth kind="error" title="Selecione um mês cota para prosseguir." />
                )}
              </>
            )}
          </S.Content>

          <S.Footer>
            <S.ButtonWrapper>
              <SlimButton
                content="Voltar"
                icon={<Icon icon="undo" width="15" />}
                onClick={handleClose}
                variant="secondary"
              />
            </S.ButtonWrapper>
            <S.ButtonWrapper>
              {selectedReport === 'pending' ? (
                <SlimButton
                  content="Baixar"
                  icon={<Icon icon="download" width="15" />}
                  onClick={getCurrentNDReport}
                  variant="primary"
                />
              ) : (
                <SlimButton
                  content="Confirmar"
                  disabled={!formState.isValid}
                  loading={loadingReport}
                  onClick={submitForm}
                  variant="primary"
                />
              )}
            </S.ButtonWrapper>
          </S.Footer>
        </ModalContentLayout>
      )}
    </>
  )
}

export default ModalReport
